export { getBookingAuthToken, shouldClearCustomerSession } from "./auth";

/**
 * Utilitários para página pública
 */

import { API_BASE, AVAILABILITY_DAYS_AHEAD } from "../constants";

/**
 * Resolve URL de imagem (relative → absolute)
 */
export function resolveImageUrl(url?: string | null): string {
  if (!url) return "";
  return url.startsWith("/") ? `${API_BASE}${url}` : url;
}

/**
 * Normaliza link de rede social. O WhatsApp não passa por aqui: usa
 * `buildEstablishmentWhatsappHref` (CAD-29), que recusa o que não é telefone
 * nem URL do WhatsApp em vez de devolver o valor literal.
 */
export function normalizeSocial(
  input: string,
  kind: "instagram" | "tiktok" | "youtube"
): string {
  if (!input) return "#";
  const v = input.trim();
  if (/^https?:\/\//i.test(v)) return v;

  if (kind === "instagram") {
    const handle = v.replace(/^@/, "");
    return `https://instagram.com/${handle}`;
  }
  if (kind === "tiktok") {
    const handle = v.replace(/^@/, "");
    return `https://tiktok.com/@${handle}`;
  }
  if (kind === "youtube") {
    const handle = v.replace(/^@/, "");
    return `https://youtube.com/@${handle}`;
  }
  return v;
}

/**
 * Converte hex para rgba
 */
export function hexToRgba(hex: string, alpha = 0.45): string | null {
  if (!hex) return null;
  let h = hex.trim();
  if (!h.startsWith("#")) return null;
  if (h.length === 4) {
    h = `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}`;
  }
  const m = /^#([0-9a-fA-F]{6})$/.exec(h);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Retorna data de hoje em formato YYYY-MM-DD
 */
export function todayYMD(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

/**
 * Retorna data máxima (hoje + N dias) em formato YYYY-MM-DD
 */
export function maxDateYMD(days: number = AVAILABILITY_DAYS_AHEAD): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0, 10);
}

/**
 * Gera lista de datas disponíveis baseada nos dias da semana
 */
export function generateAvailableDates(
  weeklyDays: number[],
  daysAhead: number = AVAILABILITY_DAYS_AHEAD
): string[] {
  if (!weeklyDays || weeklyDays.length === 0) return [];

  const today = new Date();
  const dates: string[] = [];

  for (let i = 0; i < daysAhead; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);

    if (weeklyDays.includes(d.getDay())) {
      const off = d.getTimezoneOffset();
      const local = new Date(d.getTime() - off * 60 * 1000);
      dates.push(local.toISOString().slice(0, 10));
    }
  }

  return dates;
}

/**
 * Formata endereço completo
 */
export function formatAddress(establishment: {
  addressLine?: string | null;
  addressNumber?: string | null;
  addressComplement?: string | null;
  neighborhood?: string | null;
  city?: string | null;
  state?: string | null;
  cep?: string | null;
}): string | null {
  const parts: string[] = [];

  if (establishment.addressLine) {
    let street = establishment.addressLine;
    if (establishment.addressNumber) street += `, ${establishment.addressNumber}`;
    if (establishment.addressComplement) street += ` ${establishment.addressComplement}`;
    parts.push(street);
  }

  if (establishment.neighborhood) parts.push(establishment.neighborhood);

  if (establishment.city) {
    let city = establishment.city;
    if (establishment.state) city += ` - ${establishment.state}`;
    parts.push(city);
  }

  if (establishment.cep) parts.push(`CEP: ${establishment.cep}`);

  return parts.length > 0 ? parts.join(", ") : null;
}

export function normalizeMapCoordinate(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const coordinate = Number(value);
  return Number.isFinite(coordinate) ? coordinate : null;
}

/**
 * Gera link do Google Maps
 */
export function generateGoogleMapsLink(lat?: unknown, lng?: unknown): string | null {
  const normalizedLat = normalizeMapCoordinate(lat);
  const normalizedLng = normalizeMapCoordinate(lng);
  if (normalizedLat === null || normalizedLng === null) return null;
  return `https://www.google.com/maps?q=${normalizedLat},${normalizedLng}`;
}

/**
 * Gera link do Waze
 */
export function generateWazeLink(lat?: unknown, lng?: unknown): string | null {
  const normalizedLat = normalizeMapCoordinate(lat);
  const normalizedLng = normalizeMapCoordinate(lng);
  if (normalizedLat === null || normalizedLng === null) return null;
  return `https://waze.com/ul?ll=${normalizedLat},${normalizedLng}&navigate=yes`;
}
